import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { SearchSuggestion, SearchCategory } from 'components';
import SearchIcon from '@tradera/blueprint/theme/icons2/search.svg?jsx';
import CloseNarrowIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';

import { trackSearch, trackResetButton } from './search.analytics';

import { STORAGE_KEYS } from 'constants/storage';
import StorageService from '../../app/data/storage-service';
import axios from 'axios';
import { debounce, logger } from 'packages';
import { getExistingQueryString } from './helper';
import { withTranslator, withUrlLocalizer } from 'static/script/app/lang';

const RecentSearches = new StorageService(STORAGE_KEYS.SEARCH_RECENT);
let CancelToken = axios.CancelToken;
let cancel;

const optionDivider = {
    id: 0,
    isTopLevel: false,
    name: '------------------',
    selected: false
};

const KEYCODE_ENTER = 13;
const KEYCODE_ESC = 27;
const KEYCODE_UP = 38;
const KEYCODE_DOWN = 40;
const NAVIGATION_KEY_CODES = [
    KEYCODE_DOWN,
    KEYCODE_UP,
    KEYCODE_ENTER,
    KEYCODE_ESC
];
const minCharsForSuggestions = 2;

class Search extends React.Component {
    static propTypes = {
        searchTerm: PropTypes.string,
        searchUrl: PropTypes.string,
        categoryPath: PropTypes.array,
        allCategories: PropTypes.array,
        currentCategoryId: PropTypes.number,
        handleCategoryChange: PropTypes.func
    };

    static defaultProps = {
        currentCategoryId: 0,
        categoryPath: [],
        allCategories: []
    };

    constructor(props) {
        super(props);

        this.getSuggestions = debounce(this.getSuggestions, 200);
        this.setSearchSuggestionsElement = this.setSearchSuggestionsElement.bind(
            this
        );
        this.searchSuggestionsElement = null;

        this.state = {
            searchTerm: props.searchTerm || '',
            initialSearchTerm: props.searchTerm || '',
            userInteracted: false,
            isSearchInputFocused: false,
            isAutomaticallyFocused: false,
            previousSearches: this.getRecentSearches(),
            searchSuggestions: {
                selectedIndex: null,
                previousSearches: this.getFilteredPreviousSearches(
                    props.searchTerm
                ),
                searches: [],
                shops: []
            }
        };
    }

    getRecentSearches = () => {
        let recent = RecentSearches.fetch().orderByTimestamp();
        return recent.splice(0, 7);
    };

    normalizeSearches = (searches = []) => {
        return searches.reduce((acc, search) => {
            if (search.categories) {
                search.categories.map(category => {
                    acc.push({
                        text: search.text,
                        category: category
                    });
                });
            } else {
                acc.push({
                    text: search.text,
                    category: null
                });
            }
            return acc;
        }, []);
    };

    getSuggestions = searchTerm => {
        if (cancel !== undefined) {
            cancel();
        }
        axios
            .get(`/search/suggestions`, {
                cancelToken: new CancelToken(function executor(c) {
                    cancel = c;
                }),
                params: {
                    q: searchTerm
                }
            })
            .then(response => {
                const normalizedSearches = this.normalizeSearches(
                    response.data.searches
                ); // Since the searches can exist in multiple categories we need to flatten the array
                this.setState({
                    searchSuggestions: {
                        selectedIndex: null,
                        previousSearches: this.state.searchSuggestions
                            .previousSearches,
                        searches: normalizedSearches || [],
                        shops: response.data.shops || []
                    }
                });
                return;
            })
            .catch(error => {
                // Ignore suggestion API errors and cancellations
                if (!('response' in error) && !axios.isCancel(error)) {
                    logger(error);
                }
            });
    };

    handleUpDownArrowKeys = keyCode => {
        const searchSuggestions = this.state.searchSuggestions;
        const nrOfSuggestions = () => {
            if (this.state.searchTerm.length >= minCharsForSuggestions) {
                return (
                    searchSuggestions.previousSearches.length +
                    searchSuggestions.searches.length +
                    searchSuggestions.shops.length +
                    2
                ); // +1 is for the hard coded advanced search link
            } else {
                return searchSuggestions.previousSearches.length + 2; // see above for + 1 explaination
            }
        };

        const selectedIndex = searchSuggestions.selectedIndex;
        let newSelectedIndex = null;
        if (keyCode === KEYCODE_DOWN) {
            const currentIndex = selectedIndex === null ? -1 : selectedIndex;
            newSelectedIndex =
                currentIndex + 1 > nrOfSuggestions() - 1 ? 0 : currentIndex + 1;
        } else if (keyCode === KEYCODE_UP) {
            const currentIndex = selectedIndex === null ? -1 : selectedIndex;
            newSelectedIndex =
                currentIndex - 1 < 0 ? nrOfSuggestions() - 1 : currentIndex - 1;
        }

        this.setState({
            searchSuggestions: {
                ...this.state.searchSuggestions,
                selectedIndex: newSelectedIndex
            }
        });
    };

    handleUserKeyUp = evt => {
        if (NAVIGATION_KEY_CODES.indexOf(evt.which) < 0) {
            return;
        }

        if (evt.which === KEYCODE_ESC) {
            evt.target.blur();
            // TODO: set this.state.searchTerm to this.state.initialSearchTerm?
            return;
        }

        if (evt.which === KEYCODE_ENTER) {
            const suggestions =
                this.searchSuggestionsElement &&
                this.searchSuggestionsElement.querySelectorAll(
                    '[data-suggestion]'
                );

            if (
                suggestions &&
                suggestions[this.state.searchSuggestions.selectedIndex]
            ) {
                evt.preventDefault();
                suggestions[this.state.searchSuggestions.selectedIndex].click();
            }
            return;
        }

        if (evt.which === KEYCODE_DOWN || evt.which === KEYCODE_UP) {
            this.handleUpDownArrowKeys(evt.which);
        }
    };

    getFilteredPreviousSearches = (searchTerm, limit = 7) => {
        const prev = this.getRecentSearches();

        const isSearchItem = item => {
            //if no searchterm or searchterm matches in history include this item
            if (
                (searchTerm.length > 0 && item.key.includes(searchTerm)) ||
                searchTerm.length === 0
            ) {
                return item;
            } else {
                return false;
            }
        };

        if (searchTerm) {
            return prev
                ? prev.filter(item => isSearchItem(item)).splice(0, limit)
                : [];
        }
        return prev ? prev.splice(0, limit) : [];
    };

    handleUserInputChange = evt => {
        const searchTerm = evt.target.value;
        let filteredPreviousSearches = [];
        if (searchTerm.length < 1) {
            filteredPreviousSearches = this.getFilteredPreviousSearches(
                searchTerm,
                7
            );
        } else {
            filteredPreviousSearches = this.getFilteredPreviousSearches(
                searchTerm,
                3
            );
        }

        //@TODO validation
        let newState = {
            ...this.state,
            searchTerm: searchTerm,
            userInteracted: true,
            isAutomaticallyFocused: false,
            previousSearches: this.getRecentSearches(),
            searchSuggestions: {
                ...this.state.searchSuggestions,
                previousSearches: filteredPreviousSearches
            }
        };

        if (searchTerm.length >= minCharsForSuggestions) {
            this.getSuggestions(searchTerm);
        } else {
            newState.searchSuggestions = {
                ...newState.searchSuggestions,
                searches: [],
                shops: []
            };
        }

        this.setState(newState);
    };

    handleBlur = () => {
        const onBlur = () => {
            this.setState({
                userInteracted: false,
                isSearchInputFocused: false,
                isAutomaticallyFocused: false
            });
        };

        window.setTimeout(onBlur, 100);
    };

    handleFocus = () => {
        this.setState({
            isSearchInputFocused: true,
            userInteracted: true,
            searchSuggestions: {
                ...this.state.searchSuggestions,
                selectedIndex: null
            }
        });
    };

    addRecentSearch = () => {
        if (!this.state.searchTerm) {
            return;
        }

        const prettySearchTerm = this.state.searchTerm
            .toString()
            .replace(/</g, ' ')
            .replace(/>/g, ' ')
            .trim();

        if (prettySearchTerm.length > 2) {
            let recentCount = RecentSearches.get(prettySearchTerm) || 0;
            recentCount += 1;
            RecentSearches.set(prettySearchTerm, recentCount);
        }
    };

    handleFormSubmit = evt => {
        const { currentCategoryId, toLocalizedUrl } = this.props;
        evt.preventDefault();
        this.addRecentSearch();
        this.setState({
            previousSearches: this.getRecentSearches()
        });

        const { searchTerm, initialSearchTerm } = this.state;

        // Query reformulation = First "Nike" and then "Nike air" or the other way around
        const isQueryReformulation =
            searchTerm.indexOf(initialSearchTerm) === 0 ||
            (initialSearchTerm && initialSearchTerm.indexOf(searchTerm) === 0);
        const searchParamsAsQueryStringParams = getExistingQueryString(
            this.props.searchUrl
        );
        const baseUrl =
            '/search?q=' +
            encodeURIComponent(searchTerm) +
            (currentCategoryId ? '&categoryId=' + currentCategoryId : '');
        const searchUrl =
            isQueryReformulation && searchParamsAsQueryStringParams
                ? baseUrl + '&' + searchParamsAsQueryStringParams
                : baseUrl;
        trackSearch();
        window.location.href = toLocalizedUrl(searchUrl);
    };

    handleClearSearchInput = e => {
        trackResetButton();
        e.preventDefault();
        this.setState({
            searchTerm: '',
            userInteracted: false
        });
    };

    handleSuggestionsChangingSearchTerm = () => {};

    setSearchSuggestionsElement = el => {
        this.searchSuggestionsElement = el;
    };

    componentWillReceiveProps(nextProps) {
        if (nextProps.searchTerm !== this.props.searchTerm) {
            this.setState({
                searchTerm: nextProps.searchTerm || '',
                initialSearchTerm: nextProps.searchTerm
            });
        }
    }

    getOptionAllCategories = () => ({
        id: 0,
        name: this.props.t('searchSelect_allCategories'),
        isTopLevel: false,
        selected: false
    });

    selectGenerator = (allCategories, categoryPath, currentCategoryId) => {
        /**
         * Strip "all categories" (may be multiple) from categorypath
         * Add rest of categorypath
         * Add "all categories"
         * Add all level1 categories
         */
        const selectOptions = [
            ...categoryPath.filter(category => category.id !== 0),
            this.getOptionAllCategories(),
            optionDivider,
            ...allCategories
        ];

        /**
         * Get selected category from list, fallback to "all categories"
         */
        const selectedCategory =
            selectOptions.find(category => category.id === currentCategoryId) ||
            this.getOptionAllCategories();

        return {
            selectedCategory,
            selectOptions
        };
    };

    render() {
        const {
            categoryPath,
            allCategories,
            currentCategoryId,
            handleCategoryChange,
            t,
            toLocalizedUrl
        } = this.props;

        const { selectedCategory, selectOptions } = this.selectGenerator(
            allCategories,
            categoryPath,
            currentCategoryId
        );

        const { searchTerm, initialSearchTerm } = this.state;

        const showClearButton =
            searchTerm === initialSearchTerm && searchTerm !== '';

        const placeHolderTranslation =
            selectedCategory.id !== 0
                ? t('siteHeader_categorySearchPlaceholder', {
                      categoryName: selectedCategory.name
                  })
                : t('siteHeader_generalSearchPlaceholder');

        return (
            <form
                action={toLocalizedUrl('/search')}
                method="GET"
                autoComplete="off"
                onMouseLeave={this.handleMouseLeave}
                onSubmit={this.handleFormSubmit}
                className="slim-header__search-form row no-gutters"
                data-nav-search>
                <div className="d-none d-md-flex col-md-auto">
                    <SearchCategory
                        handleCategoryChange={handleCategoryChange}
                        chosenCategory={selectedCategory}
                        selectedCategoryId={currentCategoryId}
                        selectOptions={selectOptions}
                    />
                </div>
                <div className="col">
                    <input
                        type="text"
                        className="slim-header__search-field form-control"
                        data-search-field
                        name="q"
                        placeholder={placeHolderTranslation}
                        maxLength="98"
                        tabIndex="2"
                        role="search"
                        ref={input => {
                            this.searchInput = input;
                        }}
                        value={this.state.searchTerm}
                        onBlur={this.handleBlur}
                        onChange={this.handleUserInputChange}
                        onFocus={this.handleFocus}
                        onKeyDown={this.handleUserKeyUp}
                    />
                    <fieldset className="slim-header__form-buttons">
                        {showClearButton ? (
                            <button
                                type="reset"
                                data-search-clear
                                className="slim-header__clear-button h-100 unbutton"
                                onClick={e => this.handleClearSearchInput(e)}>
                                <CloseNarrowIcon className="icon icon-sm align-middle" />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                data-search-submit
                                className="slim-header__submit-button flip-after unbutton d-inline-block">
                                <SearchIcon className="icon" />
                            </button>
                        )}
                    </fieldset>
                    <SearchSuggestion
                        filteredPreviousSearches={
                            this.state.searchSuggestions.previousSearches
                        }
                        suggestedSearches={
                            this.state.searchSuggestions.searches
                        }
                        suggestedShops={this.state.searchSuggestions.shops}
                        selectedIndex={
                            this.state.searchSuggestions.selectedIndex
                        }
                        searchTerm={this.state.searchTerm}
                        onChangeSearchTerm={
                            this.handleSuggestionsChangingSearchTerm
                        }
                        isSearchInputFocused={this.state.isSearchInputFocused}
                        setSearchSuggestionsElement={
                            this.setSearchSuggestionsElement
                        }
                        selectedCategoryId={currentCategoryId}
                    />
                </div>
            </form>
        );
    }
}

export { Search };
export default compose(
    withUrlLocalizer,
    withTranslator
)(Search);
